import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';

const SocketContext = createContext<{
  socket: Socket | null;
  emit: (event: string, data: any) => void;
  isConnected: boolean;
} | null>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.warn(`[${new Date().toISOString()}] No token, socket connection skipped`);
      return;
    }

    // الحل النهائي 100% شغال مع Caddy + HTTPS
    const socketInstance: Socket = io('https://aljodia.com', {
      auth: { token },                    // يتبعت في handshake.auth.token
      transports: ['websocket'],
      autoConnect: false,
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    socketInstance.on('connect', () => {
      console.log(`[${new Date().toISOString()}] Socket connected: ${socketInstance.id}`);
      setIsConnected(true);
    });

    socketInstance.on('connect_error', (err) => {
      console.error(`[${new Date().toISOString()}] Socket connect error:`, err.message);
      setIsConnected(false);
      toast.error(`فشل الاتصال: ${err.message}`, { toastId: 'socket-error', autoClose: 5000 });
    });

    socketInstance.on('disconnect', (reason) => {
      console.log(`[${new Date().toISOString()}] Socket disconnected: ${reason}`);
      setIsConnected(false);
    });

    socketInstance.on('reconnect', (attempt) => {
      console.log(`[${new Date().toISOString()}] Reconnected after ${attempt} attempts`);
      setIsConnected(true);
      toast.success('تم إعادة الاتصال', { toastId: 'socket-reconnected', autoClose: 2000 });
    });

    setSocket(socketInstance);
    socketInstance.connect();

    return () => {
      socketInstance.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [isAuthenticated, user]);

  const emit = (event: string, data: any) => {
    if (!socket || !isConnected) {
      console.warn(`[${new Date().toISOString()}] Cannot emit ${event}: Not connected`);
      return;
    }
    const payload = { ...data, eventId: data.eventId || crypto.randomUUID() };
    console.log(`[${new Date().toISOString()}] Emitting: ${event}`, payload);
    socket.emit(event, payload);
  };

  return (
    <SocketContext.Provider value={{ socket, emit, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) throw new Error('useSocket يجب أن يُستخدم داخل SocketProvider');
  return context;
};